import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Banknote, Boxes, HandCoins, Receipt, TrendingUp } from "lucide-react";
import { StatCard } from "@/components/common/StatCard";
import { EmptyState } from "@/components/common/EmptyState";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { shopApi } from "@/lib/api";
import { apiErrorMessage } from "@/lib/api/errors";
import { formatMoney, formatNumber } from "@/lib/utils";

const METHOD_LABELS: Record<string, string> = {
  CASH: "Cash",
  MOBILE: "Mobile money",
  CARD: "Card",
  CREDIT: "Credit (debt)",
};

/** A shop's day at a glance: sales, profit, stock, debts, best sellers. Read-only. */
export function ShopSalesTab({ tenantId }: { tenantId: number }) {
  const summary = useQuery({
    queryKey: ["shop-summary", tenantId],
    queryFn: () => shopApi.summary(tenantId),
    refetchInterval: 60_000,
  });

  if (summary.isLoading) return <Skeleton className="h-64" />;
  if (summary.error || !summary.data) {
    return <EmptyState icon={Receipt} title="Couldn't load sales" description={apiErrorMessage(summary.error)} />;
  }
  const s = summary.data;

  return (
    <div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard label="Sales today" value={formatMoney(s.todayTotal)} icon={Banknote} tone="success" />
        <StatCard label="Gross profit today" value={formatMoney(s.todayProfit)} icon={TrendingUp} />
        <StatCard label="Sales count today" value={formatNumber(s.todayCount)} icon={Receipt} />
        <StatCard label="Stock value (at cost)" value={formatMoney(s.stockValue)} icon={Boxes} />
        <StatCard
          label={`Owed by customers (${s.debtCount})`}
          value={formatMoney(s.outstandingDebt)}
          icon={HandCoins}
          tone={s.overdueDebtCount > 0 ? "warning" : "default"}
        />
        <StatCard
          label="Out of stock / running low"
          value={`${formatNumber(s.outOfStockCount)} / ${formatNumber(s.lowStockCount)}`}
          icon={AlertTriangle}
          tone={s.outOfStockCount > 0 ? "warning" : "default"}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>This week and month</CardTitle>
            <CardDescription>Completed sales; cancelled ones are left out</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="This week" value={formatMoney(s.weekTotal)} />
            <Row label="This month" value={formatMoney(s.monthTotal)} />
            <Row label="Debt collected today" value={formatMoney(s.todayDebtCollected)} />
            {s.todayByMethod.map((m) => (
              <Row key={m.method} label={`Today — ${METHOD_LABELS[m.method] ?? m.method}`} value={formatMoney(m.total)} />
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Best sellers today</CardTitle>
            <CardDescription>By revenue</CardDescription>
          </CardHeader>
          <CardContent>
            {s.topSellers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No sales yet today.</p>
            ) : (
              <div className="space-y-2">
                {s.topSellers.map((t, i) => (
                  <div key={t.itemId} className="flex items-center gap-3 text-sm">
                    <span className="w-5 shrink-0 text-muted-foreground">{i + 1}</span>
                    <span className="min-w-0 flex-1 truncate font-medium">{t.itemName}</span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">{formatNumber(t.quantity)}×</span>
                    <span className="shrink-0 font-semibold tabular-nums">{formatMoney(t.revenue)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}
